import { useState } from 'react';
import { ouvreDocument } from '../api/documents.js';
import { tr } from '../lib/i18n';
import { Btn } from './ui/Btn.jsx';

/**
 * Bouton qui ouvre le document officiel produit par le serveur.
 *
 * <p>La pièce dessinée dans la page est un aperçu ; celle-ci est le document
 * figé, conservé et opposable. Le refus du serveur — un brouillon que l'on ne
 * peut pas émettre, par exemple — est montré à l'utilisateur plutôt que d'ouvrir
 * un onglet vide.
 *
 * @param {object} props propriétés du bouton
 * @param {string} props.chemin chemin du document sous `/api`
 * @param {object} [props.params] paramètres de requête
 * @param {string} [props.label] libellé du bouton
 * @param {string} [props.color] couleur du bouton
 * @param {boolean} [props.full] bouton pleine largeur
 * @param {boolean} [props.sm] bouton compact
 * @param {Function} [props.avant] traitement à exécuter avant l'ouverture ;
 *   son résultat remplace le chemin s'il en renvoie un
 * @param {Function} [props.corps] contenu du document, évalué au clic ; la
 *   requête passe alors en POST. Une fonction, et non une valeur : le contenu
 *   suit un formulaire que l'utilisateur remplit sous les yeux du bouton.
 * @param {Function} [props.confirmation] question posée au clic, évaluée à ce
 *   moment ; sans réponse positive, rien n'est émis. Un document remis à
 *   l'employé dès son émission ne doit pas partir quand on voulait le relire.
 * @returns {JSX.Element} le bouton
 */
export function BoutonDocument({
  chemin,
  params,
  label = '📄 PDF officiel',
  color,
  full,
  sm,
  avant,
  corps,
  confirmation,
  // Après une émission réussie (inscrire la date de départ, relire la liste…).
  apres,
}) {
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState('');

  const ouvre = async () => {
    const question = confirmation ? confirmation() : '';
    if (question && !window.confirm(tr(question))) return;
    setEnCours(true);
    setErreur('');
    try {
      const vise = avant ? await avant() : chemin;
      await ouvreDocument(vise || chemin, params, corps ? corps() : undefined);
      if (apres) apres();
    } catch (e) {
      setErreur(e?.message || "Le document n'a pas pu être produit.");
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div style={full ? { width: '100%' } : undefined}>
      <Btn
        label={enCours ? '⏳ Préparation…' : label}
        color={color}
        full={full}
        sm={sm}
        disabled={enCours}
        onClick={ouvre}
      />
      {erreur && (
        <div style={{ fontSize: 11, color: '#C0392B', marginTop: 4, lineHeight: 1.4 }}>
          {erreur}
        </div>
      )}
    </div>
  );
}
